import React, { useState, useEffect } from 'react';
import { userService, leaveService } from '../services/api.service';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectOption } from '../components/ui/select';
import AppLayout from '../layouts/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const StaffManagementPage = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [managers, setManagers] = useState([]);
  const [sortBy, setSortBy] = useState('name'); // Added sorting functionality
  const [sortDirection, setSortDirection] = useState('asc');

  // Fetch staff data
  useEffect(() => {
    const fetchStaffData = async () => {
      try {
        setLoading(true);
        const [usersResponse, managersResponse] = await Promise.all([
          userService.getAllUsers(),
          userService.getManagers()
        ]);
        
        // Filter employees based on user role
        let staffList = usersResponse.users || [];
        
        // If manager, only show employees from their department
        if (user.role === 'manager') {
          staffList = staffList.filter(emp => emp.department === user.department);
        }
        
        setEmployees(staffList);
        setFilteredEmployees(staffList);
        setManagers(managersResponse.managers || []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching staff data:', err);
        setError('Failed to load staff information');
        setLoading(false);
      }
    };
    
    fetchStaffData();
  }, [user.role, user.department]);
  
  // Apply filters when search or department filter changes
  useEffect(() => {
    let filtered = [...employees];
    
    // Apply department filter
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(emp => emp.department === departmentFilter);
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(emp => 
        emp.name.toLowerCase().includes(query) || 
        emp.email.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let valA = a[sortBy] || '';
      let valB = b[sortBy] || '';
      
      // Special handling for dates
      if (sortBy === 'join_date' || sortBy === 'birthdate') {
        valA = valA ? new Date(valA).getTime() : 0;
        valB = valB ? new Date(valB).getTime() : 0;
      }
      
      // Handle numbers
      if (sortBy === 'leave_balance') {
        valA = Number(valA) || 0;
        valB = Number(valB) || 0;
      }
      
      // Apply sort direction
      if (sortDirection === 'asc') {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });
    
    setFilteredEmployees(filtered);
  }, [departmentFilter, searchQuery, employees, sortBy, sortDirection]);
  
  // Handle department filter change
  const handleDepartmentChange = (e) => {
    setDepartmentFilter(e.target.value);
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle sort change
  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle direction if already sorting by this field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field
      setSortBy(field);
      setSortDirection('asc');
    }
  };
  
  // Get unique departments
  const departments = ['all', ...new Set(employees
    .map(emp => emp.department)
    .filter(Boolean)
  )];
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // View employee details or leave requests
  const viewEmployeeLeaveRequests = (employeeId) => {
    // Navigate to the new leave history page
    window.location.href = `/staff/leave-history/${employeeId}`;
  };
  
  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading staff data...</p>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl font-bold">Staff Management</h1>
            <p className="text-gray-500 mt-1">Manage employee information and leave balances</p>
          </div>
          
          {user.role === 'admin' && (
            <Link to="/register">
              <Button className="bg-indigo-600 hover:bg-indigo-700">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add New Employee
              </Button>
            </Link>
          )}
        </div>
        
        {error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-md border border-red-200 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}
        
        {/* Filters */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Filters and Search</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0">
              <div className="w-full md:w-1/2">
                <div className="relative">
                  <Input
                    placeholder="Search by name or email"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="pl-10"
                  />
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium whitespace-nowrap">Department:</label>
                <Select
                  value={departmentFilter}
                  onChange={handleDepartmentChange}
                  className="min-w-[160px]"
                >
                  <SelectOption value="all">All Departments</SelectOption>
                  {departments.filter(dept => dept !== 'all').map(dept => (
                    <SelectOption key={dept} value={dept}>{dept}</SelectOption>
                  ))}
                </Select>
              </div>
              
              <div className="flex items-center space-x-2 ml-auto">
                <label className="text-sm font-medium whitespace-nowrap">Sort by:</label>
                <Select
                  value={sortBy}
                  onChange={(e) => handleSort(e.target.value)}
                  className="min-w-[140px]"
                >
                  <SelectOption value="name">Name</SelectOption>
                  <SelectOption value="department">Department</SelectOption>
                  <SelectOption value="join_date">Join Date</SelectOption>
                  <SelectOption value="leave_balance">Leave Balance</SelectOption>
                </Select>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                  className="px-2"
                >
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </Button>
              </div>
            </div>
            
            <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
              <span>Showing {filteredEmployees.length} of {employees.length} employees</span>
              {searchQuery && <span>Search results for: "{searchQuery}"</span>}
            </div>
          </CardContent>
        </Card>
        
        {/* Staff List */}
        <Card className="shadow-sm overflow-hidden">
          <CardHeader className="bg-gray-50 pb-2">
            <CardTitle>Employee Directory</CardTitle>
          </CardHeader>
          
          <CardContent className="p-0">
            {filteredEmployees.length > 0 ? (
              <div className="divide-y">
                {filteredEmployees.map(employee => (
                  <div 
                    key={employee.id} 
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                          <h3 className="font-medium text-lg text-indigo-700">{employee.name}</h3>
                          <div className="flex flex-wrap gap-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                              {employee.role}
                            </span>
                            {employee.department && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {employee.department}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                          <div>
                            <p className="text-gray-500 font-medium">Email</p>
                            <p className="truncate">{employee.email}</p>
                          </div>
                          
                          <div>
                            <p className="text-gray-500 font-medium">Manager</p>
                            <p>{employee.manager_name || 'Not assigned'}</p>
                          </div>
                          
                          <div>
                            <p className="text-gray-500 font-medium">Join Date</p>
                            <p>{formatDate(employee.join_date)}</p>
                          </div>
                          
                          <div>
                            <p className="text-gray-500 font-medium">Leave Balance</p>
                            <p className="font-medium text-green-600">{employee.leave_balance || 0} days</p>
                          </div>
                          
                          {employee.birthdate && (
                            <div>
                              <p className="text-gray-500 font-medium">Birthday</p>
                              <p>{formatDate(employee.birthdate)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="mt-4 md:mt-0 md:ml-4 flex flex-row md:flex-col space-x-2 md:space-x-0 md:space-y-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => viewEmployeeLeaveRequests(employee.id)}
                          className="flex-1 md:flex-none"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Leave History
                        </Button>
                        
                        {user.role === 'admin' && (
                          <Link to={`/admin/edit-user/${employee.id}`} className="flex-1 md:flex-none">
                            <Button size="sm" variant="outline" className="w-full">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-lg font-medium">No employees found</p>
                <p className="mt-1">Try adjusting your search or filter criteria</p>
                {searchQuery && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-4"
                    onClick={() => setSearchQuery('')}
                  >
                    Clear Search
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default StaffManagementPage;